import React, { useState, useEffect, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import api from "../../services/api";
import type { Club } from "../../services/clubs";
import {
  previewPushCampaign,
  sendPushCampaign,
  listPushCampaigns,
  type CampaignFilters,
  type CampaignPreview,
  type PushCampaign,
} from "../../services/pushCampaigns";

const ALL_ROLES: { code: string; label: string }[] = [
  { code: "platform_admin", label: "Admin platformă" },
  { code: "club_admin", label: "Admin club" },
  { code: "coach", label: "Antrenor" },
  { code: "athlete", label: "Sportiv" },
];

const ALL_PLATFORMS: { code: string; label: string }[] = [
  { code: "android", label: "Android" },
  { code: "ios", label: "iOS" },
];

export default function Notifications() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [clubFilter, setClubFilter] = useState<string>("");
  const [clubs, setClubs] = useState<Club[]>([]);

  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const [history, setHistory] = useState<PushCampaign[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<Club[]>("/clubs");
        const flat = Array.isArray(data)
          ? data.map((row: any) => row?.club ?? row).filter(Boolean)
          : [];
        setClubs(flat);
      } catch (e) {
        console.error("Failed to load clubs:", e);
      }
    })();
    loadHistory();
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await listPushCampaigns({ page: 1, pageSize: 20 });
      setHistory(data);
    } catch (e) {
      console.error("Failed to load history:", e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const filters = useMemo<CampaignFilters>(
    () => ({
      target_roles: selectedRoles,
      target_platforms: selectedPlatforms,
      target_club_id: clubFilter || null,
    }),
    [selectedRoles, selectedPlatforms, clubFilter]
  );

  // Debounce preview so the count updates as filters change without
  // hammering the backend on every keystroke.
  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await previewPushCampaign(filters);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters]);

  const toggleRole = (code: string) => {
    setSelectedRoles((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
  };
  const togglePlatform = (code: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]
    );
  };

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const result = await sendPushCampaign({ title: title.trim(), body: body.trim(), ...filters });
      if (result.device_count === 0) {
        // Nobody has a registered push device — don't pretend it was sent.
        toast.error("Niciun dispozitiv înregistrat pentru filtrele selectate — notificarea nu a ajuns la nimeni.");
      } else if (result.sent_count === 0) {
        toast.error(`Trimiterea a eșuat către toate cele ${result.device_count} dispozitive.`);
      } else {
        toast.success(
          `Trimis către ${result.sent_count} din ${result.device_count} dispozitive` +
            (result.failed_count > 0 ? ` (${result.failed_count} eșuate)` : "")
        );
      }
      setTitle("");
      setBody("");
      setConfirming(false);
      loadHistory();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Eroare la trimitere");
    } finally {
      setSending(false);
    }
  };

  const platformSummary = (c: PushCampaign) => {
    if (!c.target_platforms || c.target_platforms.length === 0) return "Toate";
    return c.target_platforms.map((p) => (p === "ios" ? "iOS" : "Android")).join(" + ");
  };
  const roleSummary = (c: PushCampaign) => {
    if (!c.target_roles || c.target_roles.length === 0) return "Toți";
    return c.target_roles
      .map((r) => ALL_ROLES.find((x) => x.code === r)?.label || r)
      .join(" + ");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-xl">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Notificări push</h1>
            <p className="text-slate-500 text-sm mt-1">
              Trimite notificări către utilizatorii mobile — Android și iOS
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Compune notificare</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Titlu</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={65}
                  placeholder="ex: Quiz nou disponibil"
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-slate-400 mt-1">{title.length}/65 caractere</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Mesaj</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  maxLength={240}
                  placeholder="ex: A apărut un quiz nou — hai să vedem cine termină primul!"
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
                <p className="text-xs text-slate-400 mt-1">{body.length}/240 caractere</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Rol — selectează unul sau mai multe (gol = toți)
                </label>
                <div className="flex flex-wrap gap-2">
                  {ALL_ROLES.map((r) => {
                    const selected = selectedRoles.includes(r.code);
                    return (
                      <button
                        key={r.code}
                        type="button"
                        onClick={() => toggleRole(r.code)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                          selected
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                        }`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Platformă (gol = ambele)
                </label>
                <div className="flex flex-wrap gap-2">
                  {ALL_PLATFORMS.map((p) => {
                    const selected = selectedPlatforms.includes(p.code);
                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => togglePlatform(p.code)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                          selected
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Club (opțional)
                </label>
                <select
                  value={clubFilter}
                  onChange={(e) => setClubFilter(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Toate cluburile</option>
                  {clubs
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>

              <button
                type="button"
                disabled={!canSend}
                onClick={() => setConfirming(true)}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trimite notificarea
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sticky top-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
                Previzualizare trimitere
              </h3>

              <div className="space-y-4">
                {/* Count */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl">
                  <p className="text-xs text-slate-500 mb-1">Va ajunge la</p>
                  {previewLoading ? (
                    <div className="h-8 w-24 bg-white/60 rounded animate-pulse" />
                  ) : preview ? (
                    <>
                      <p className="text-3xl font-bold text-slate-800">{preview.device_count}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        dispozitive · {preview.user_count} utilizatori
                      </p>
                    </>
                  ) : (
                    <p className="text-slate-400 text-sm">—</p>
                  )}
                </div>

                {/* Breakdown */}
                {preview && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                      <p className="text-xs text-emerald-700">Android</p>
                      <p className="text-lg font-bold text-emerald-800">{preview.breakdown.android}</p>
                    </div>
                    <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                      <p className="text-xs text-slate-700">iOS</p>
                      <p className="text-lg font-bold text-slate-800">{preview.breakdown.ios}</p>
                    </div>
                  </div>
                )}

                {/* Message preview */}
                <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0 text-xs">
                      iG
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 leading-tight">
                        {title || "Titlul notificării"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 break-words">
                        {body || "Mesajul apare aici..."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800">Istoric campanii</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Ultimele 20 de campanii trimise
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Titlu</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Roluri</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Platforme</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Trimis</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Eșuat</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Trimis de</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {historyLoading ? (
                  <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500">Se încarcă...</td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500">Nicio campanie trimisă</td></tr>
                ) : (
                  history.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-6 py-3">
                        <p className="text-sm font-medium text-slate-800">{c.title}</p>
                        <p className="text-xs text-slate-500 truncate max-w-xs">{c.body}</p>
                      </td>
                      <td className="px-6 py-3 text-sm text-slate-700">{roleSummary(c)}</td>
                      <td className="px-6 py-3 text-sm text-slate-700">{platformSummary(c)}</td>
                      <td className="px-6 py-3 text-right text-sm font-semibold">
                        {c.device_count === 0 ? (
                          <span className="text-amber-600" title="Niciun dispozitiv înregistrat — nu a ajuns la nimeni">0 disp.</span>
                        ) : (
                          <span className={c.sent_count > 0 ? "text-emerald-600" : "text-red-500"}>
                            {c.sent_count}/{c.device_count}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right text-sm text-red-500">
                        {c.failed_count > 0 ? c.failed_count : "-"}
                      </td>
                      <td className="px-6 py-3 text-sm text-slate-500 whitespace-nowrap">
                        {new Date(c.sent_at).toLocaleString("ro-RO", {
                          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                      </td>
                      <td className="px-6 py-3 text-sm text-slate-500">
                        {c.created_by_user?.full_name || c.created_by_user?.email || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {confirming && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Confirmă trimiterea</h3>
            <p className="text-sm text-slate-600 mb-4">
              Notificarea va fi trimisă către <span className="font-bold">{preview?.device_count ?? 0}</span> dispozitive
              {preview && (
                <>
                  {" "}
                  ({preview.breakdown.android} Android, {preview.breakdown.ios} iOS)
                </>
              )}
              . Nu poți anula după ce apesi Trimite.
            </p>
            {(preview?.device_count ?? 0) === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 flex items-start gap-2">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /></svg>
                <p className="text-xs text-amber-800">
                  Niciun dispozitiv înregistrat pentru aceste filtre. Notificarea <span className="font-semibold">nu va ajunge la nimeni</span> (push funcționează doar în aplicația mobilă, pentru utilizatorii care au acordat permisiunea).
                </p>
              </div>
            )}
            <div className="p-3 bg-slate-50 rounded-lg mb-4">
              <p className="text-xs text-slate-500 mb-1">Previzualizare:</p>
              <p className="text-sm font-semibold text-slate-800">{title}</p>
              <p className="text-xs text-slate-600 mt-0.5">{body}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={sending}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                Anulează
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {sending ? "Se trimite..." : "Trimite acum"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
